import { EducationExperienceGraduateType } from '@flex-apis/recruiting';
import {
  FieldBox,
  FormField,
  PrimitiveButton,
  Select,
  Typography,
} from '@flex-design-system/fx';
import {
  ChevronDownMiniIcon,
  SchoolAltIcon,
} from '@flex-design-system/legacy-flex-icons';
import { Translation, useTranslation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';
import { EDUCATIONAL_BACKGROUND_GRADUATION_STATUS_CONTENT } from '@flex-private-packages/recruiting-common/base';
import { Controller } from 'react-hook-form';

import type { JobDescriptionFormState } from '../../../../models';

interface Props {
  currentIndex: number;
  required: boolean;
}

const EDUCATION_GRADUATION_STATUS_TYPES: Array<EducationExperienceGraduateType> =
  [
    EducationExperienceGraduateType.Absence,
    EducationExperienceGraduateType.Attending,
    EducationExperienceGraduateType.DropOut,
    EducationExperienceGraduateType.Graduated,
    EducationExperienceGraduateType.PlanToGraduate,
    EducationExperienceGraduateType.Unknown,
  ];

export function EducationGraduateStatusTypeSelectFormItem({
  currentIndex,
  required,
}: Props) {
  const { t } = useTranslation();

  return (
    <Controller<
      JobDescriptionFormState,
      `educationalBackgrounds.${typeof currentIndex}.graduationStatus`
    >
      name={`educationalBackgrounds.${currentIndex}.graduationStatus`}
      rules={{
        required: required
          ? t(
              trans(
                'recruiting.educational_background.graduate_status.edit.helper.required'
              )
            )
          : false,
      }}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error },
      }) => (
        <FormField required={required} state={error ? 'error' : 'default'}>
          <FormField.Label leftSlot={<SchoolAltIcon />}>
            <Translation
              tKey={trans('recruiting.educational_background.graduate_status')}
            />
          </FormField.Label>
          <Select value={value} onValueChange={onChange} {...field}>
            <Select.Trigger asChild>
              <FormField.FieldBox asChild>
                <PrimitiveButton css={{ width: '100%' }}>
                  <FieldBox.Content>
                    {value ? (
                      <Translation
                        tKey={
                          EDUCATIONAL_BACKGROUND_GRADUATION_STATUS_CONTENT[
                            value
                          ].i18n
                        }
                      />
                    ) : (
                      <Typography color="gray" asChild>
                        <Translation
                          tKey={trans(
                            'recruiting.educational_background.graduate_status.edit.placeholder'
                          )}
                        />
                      </Typography>
                    )}
                  </FieldBox.Content>
                  <FieldBox.RightSlotRoot>
                    <ChevronDownMiniIcon size={22} color="grayLight" />
                  </FieldBox.RightSlotRoot>
                </PrimitiveButton>
              </FormField.FieldBox>
            </Select.Trigger>
            <Select.Content fitToTrigger>
              {EDUCATION_GRADUATION_STATUS_TYPES.map(statusType => (
                <Select.Option key={statusType} value={statusType}>
                  <Select.OptionTitle>
                    <Translation
                      tKey={
                        EDUCATIONAL_BACKGROUND_GRADUATION_STATUS_CONTENT[
                          statusType
                        ].i18n
                      }
                    />
                  </Select.OptionTitle>
                </Select.Option>
              ))}
            </Select.Content>
          </Select>
          {error?.message && (
            <FormField.HelperText>{error.message}</FormField.HelperText>
          )}
        </FormField>
      )}
    />
  );
}
